import classNames from "classnames";
import { FunctionComponent } from "react";
import RadioButton from "rivals/components/shared/Form/RadioButton";
import AddOnOffers from "rivals/components/shared/Subscription/AddOnOffers";
import { PLANS, PurchasablePlan } from "rivals/shared/constants";
import { Promotion } from "rivals/shared/interfaces/Promotion";
import { setData } from "rivals/shared/utils/qa";
import styles from "./SubscriptionPlan.module.scss";
import strings from "./strings";
import { PlanProps, PlanWithRadio, SelectionProps } from "./types";

export const SubscriptionPlan: FunctionComponent<PlanProps> = ({
  bullets,
  cost,
  savings
}): JSX.Element => {
  return (
    <div className={styles.container}>
      <span className={styles.cost} {...setData("subscription-plan", "cost")}>
        {cost}
      </span>
      {savings && (
        <span
          className={styles.savings}
          {...setData("subscription-plan", "savings")}
        >{` ${savings}`}</span>
      )}
      <ul className={styles.bullets}>
        {bullets.map((bullet, idx) => (
          <li {...setData("subscription-plan-bullet")} key={idx}>
            {bullet}
          </li>
        ))}
      </ul>
    </div>
  );
};

const buttonDisabled = (
  planType?: PurchasablePlan,
  promotion?: Promotion
): boolean => {
  return !!(
    promotion &&
    ((planType === PLANS.ANNUAL && promotion.selectedPlan === PLANS.MONTHLY) ||
      (planType === PLANS.MONTHLY && promotion.selectedPlan === PLANS.ANNUAL))
  );
};

const planWithRadio = ({
  plan,
  onChange,
  promotion,
  selectedPlan
}: PlanWithRadio): JSX.Element | null => {
  const { planType, bullets, cost, savings } = plan;

  return (
    <div className={styles.selectionRow} key={planType}>
      <RadioButton
        data={`subscription-plan-${planType}-radio`}
        disabled={buttonDisabled(planType, promotion)}
        isChecked={selectedPlan === planType}
        label={
          <SubscriptionPlan bullets={bullets} cost={cost} savings={savings} />
        }
        name={`subscription-price-${planType}`}
        onChange={onChange}
        value={planType}
      />
    </div>
  );
};

const {
  fansDescriptionFirst,
  fansDescriptionLast,
  fansTitle,
  forumsDescription,
  forumsTitle,
  newsDescription,
  newsTitle
} = strings;

const benefitsSection = (
  <div className={styles.benefitsSection}>
    <div className={styles.benefit}>
      <img alt="bell icon" src={"/static/icons/subscriptions/bell.svg"} />
      <h3>{newsTitle}</h3>
      <span>{newsDescription}</span>
    </div>
    <div className={styles.benefit}>
      <img alt="forums icon" src={"/static/icons/subscriptions/forums.svg"} />
      <h3>{forumsTitle}</h3>
      <span>{forumsDescription}</span>
    </div>
    <div className={styles.benefit}>
      <img alt="trophy icon" src={"/static/icons/subscriptions/trophy.svg"} />
      <h3>{fansTitle}</h3>
      <span>{fansDescriptionFirst}</span>
      <span>{fansDescriptionLast}</span>
    </div>
  </div>
);

// TODO: https://griosf.atlassian.net/browse/RVLS-7844
export const SubscriptionPlanSelection: FunctionComponent<SelectionProps> = ({
  action,
  className,
  subscribableAddOns,
  onChange,
  plans,
  promotion,
  selectedPlan,
  withBenefits = false
}): JSX.Element => {
  const showAddOns = !!subscribableAddOns?.length;

  const containerClasses = classNames(className, styles.selectionContainer, {
    [styles.actionContainer]: action,
    [styles.benefitsContainer]: withBenefits,
    [styles.withExtraOfferContainer]: showAddOns
  });

  return (
    <>
      <div className={containerClasses}>
        {withBenefits && benefitsSection}
        <div className={styles.planActionContainer}>
          <div className={styles.planSelection}>
            {plans.map(plan =>
              planWithRadio({ onChange, plan, promotion, selectedPlan })
            )}
          </div>
          <div className={styles.action}>{action}</div>
        </div>
        {showAddOns && <AddOnOffers addOns={subscribableAddOns} />}
      </div>
    </>
  );
};
